import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Mobile-optimized CartDrawerComponent with instant visual feedback
 * 
 * MOBILE OPTIMIZATIONS:
 * 1. Opens dialog visually immediately (< 50ms)
 * 2. Shows loading state while content loads
 * 3. Disables CSS animations on mobile for instant rendering
 * 4. Prioritizes viewport rendering over off-screen content
 * 5. Cancels non-critical network requests on slow connections
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #cartUpdateAbortController = null;

  /** @type {boolean} */
  #isUpdatingCart = false;

  /** @type {boolean} */
  #isMobile = false;

  /** @type {number} */
  #networkTimeout = 8000; // 8 second timeout for slow mobile networks

  /** @type {boolean} */
  #isSlowConnection = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Detect mobile
    this.#isMobile = isMobileBreakpoint();
    
    // Detect slow connection (2G/3G)
    this.#detectConnectionSpeed();
    
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // Add visual class for mobile
    if (this.#isMobile) {
      this.classList.add('is-mobile');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#cartUpdateAbortController?.abort();
  }

  // ===========================
  // DETECT CONNECTION SPEED
  // ===========================
  #detectConnectionSpeed() {
    if (!navigator.connection) return;

    const connection = navigator.connection;
    const effectiveType = connection.effectiveType; // 'slow-2g' | '2g' | '3g' | '4g'
    
    this.#isSlowConnection = effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g';
    
    if (this.#isSlowConnection) {
      // Increase timeout for slow connections
      this.#networkTimeout = 12000; // 12 seconds
      this.classList.add('slow-connection');
    }

    // Listen for connection changes
    connection.addEventListener('change', () => {
      const newType = connection.effectiveType;
      this.#isSlowConnection = newType === 'slow-2g' || newType === '2g' || newType === '3g';
      
      if (this.#isSlowConnection) {
        this.classList.add('slow-connection');
      } else {
        this.classList.remove('slow-connection');
      }
    });
  }

  // ===========================
  // INSTANT CART ADD - MOBILE OPTIMIZED
  // ===========================
  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      // 🚀 MOBILE OPTIMIZATION: Show loading state IMMEDIATELY
      this.#showLoadingState();
      
      // Open dialog with instant visual feedback
      this.showDialog();

      // Load cart content in background (with timeout)
      this.#updateCartDrawerContent();
    }
  };

  // ===========================
  // SHOW LOADING STATE
  // ===========================
  #showLoadingState() {
    const { dialog } = /** @type {{dialog?: HTMLDialogElement}} */ (this.refs);
    if (!dialog) return;

    const contentArea = dialog.querySelector('.cart-drawer__content');
    if (!contentArea) return;

    // Add loading class for CSS styling
    contentArea.classList.add('is-loading');
    
    // Show skeleton or loading placeholder
    requestAnimationFrame(() => {
      // Force reflow to ensure loading state is visible immediately
      contentArea.offsetHeight;
    });
  }

  // ===========================
  // HIDE LOADING STATE
  // ===========================
  #hideLoadingState() {
    const { dialog } = /** @type {{dialog?: HTMLDialogElement}} */ (this.refs);
    if (!dialog) return;

    const contentArea = dialog.querySelector('.cart-drawer__content');
    if (!contentArea) return;

    contentArea.classList.remove('is-loading');
  }

  // ===========================
  // UPDATE CART IN BACKGROUND (MOBILE OPTIMIZED)
  // ===========================
  #updateCartDrawerContent = async () => {
    if (this.#isUpdatingCart) return;
    this.#isUpdatingCart = true;

    try {
      this.#cartUpdateAbortController?.abort();
      this.#cartUpdateAbortController = new AbortController();

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Cart update timeout'));
        }, this.#networkTimeout);
      });

      // Fetch with timeout
      const fetchPromise = fetch('/?section_id=cart-drawer', {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: this.#cartUpdateAbortController.signal,
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (!response || !response.ok) {
        console.warn('Cart update failed');
        this.#hideLoadingState();
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const updatedDrawer = doc.querySelector('cart-drawer-component');

      if (!updatedDrawer) {
        this.#hideLoadingState();
        return;
      }

      const { dialog } = /** @type {{dialog?: HTMLDialogElement}} */ (this.refs);
      if (!dialog) return;

      // Update content with minimal reflow
      this.#updateDrawerContent(dialog, updatedDrawer);
      this.#updateDrawerSummary(dialog, updatedDrawer);
      
      // Hide loading state
      this.#hideLoadingState();

    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Error updating cart drawer:', error);
      
      // Show error state or fallback
      this.#showErrorState();
      this.#hideLoadingState();
    } finally {
      this.#isUpdatingCart = false;
    }
  };

  // ===========================
  // UPDATE DRAWER CONTENT (OPTIMIZED)
  // ===========================
  #updateDrawerContent(dialog, updatedDrawer) {
    const contentArea = dialog.querySelector('.cart-drawer__content');
    const updatedContent = updatedDrawer.querySelector('.cart-drawer__content');

    if (!contentArea || !updatedContent) return;

    // Batch DOM updates using requestAnimationFrame
    requestAnimationFrame(() => {
      // Use innerHTML for faster updates (no event listeners needed)
      contentArea.innerHTML = updatedContent.innerHTML;
      
      // Dispatch event for any listeners
      this.dispatchEvent(new CustomEvent('cart-content-updated'));
    });
  }

  // ===========================
  // UPDATE DRAWER SUMMARY (OPTIMIZED)
  // ===========================
  #updateDrawerSummary(dialog, updatedDrawer) {
    const summaryArea = dialog.querySelector('.cart-drawer__summary');
    const updatedSummary = updatedDrawer.querySelector('.cart-drawer__summary');

    if (!summaryArea || !updatedSummary) return;

    requestAnimationFrame(() => {
      summaryArea.innerHTML = updatedSummary.innerHTML;
      this.#updateStickyState();
    });
  }

  // ===========================
  // SHOW ERROR STATE
  // ===========================
  #showErrorState() {
    const { dialog } = /** @type {{dialog?: HTMLDialogElement}} */ (this.refs);
    if (!dialog) return;

    const contentArea = dialog.querySelector('.cart-drawer__content');
    if (!contentArea) return;

    contentArea.classList.add('has-error');
  }

  // ===========================
  // HISTORY MANAGEMENT
  // ===========================
  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { 
      signal: this.#historyAbortController.signal 
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  // ===========================
  // PUBLIC API
  // ===========================
  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {{dialog?: HTMLDialogElement}} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  // ===========================
  // MANUAL REFRESH
  // ===========================
  refreshCart() {
    this.#updateCartDrawerContent();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}